import { RelatorioResponse } from './models/relatorio.models';

export type Locale = 'pt-BR' | 'en';
type Cover = 'coral' | 'teal' | 'emerald' | 'ocean' | 'sand' | 'ink';

/** Capas em gradiente do design system (protótipo). */
export const COVER_STYLES: Record<Cover, string> = {
  coral:
    'bg-gradient-to-br from-[hsl(8_75%_64%)] via-[hsl(354_70%_56%)] to-[hsl(346_65%_42%)]',
  teal: 'bg-gradient-to-br from-[hsl(180_45%_28%)] via-[hsl(190_50%_22%)] to-[hsl(200_55%_16%)]',
  emerald:
    'bg-gradient-to-br from-[hsl(168_55%_38%)] via-[hsl(160_55%_28%)] to-[hsl(155_60%_20%)]',
  ocean:
    'bg-gradient-to-br from-[hsl(195_70%_48%)] via-[hsl(205_70%_38%)] to-[hsl(215_65%_24%)]',
  sand: 'bg-gradient-to-br from-[hsl(35_55%_75%)] via-[hsl(28_50%_62%)] to-[hsl(22_45%_42%)]',
  ink: 'bg-gradient-to-br from-[hsl(195_30%_28%)] via-[hsl(200_35%_18%)] to-[hsl(210_40%_10%)]',
};

interface CategoryMeta {
  pt: string;
  en: string;
  descPt: string;
  descEn: string;
  cover: Cover;
}

/** Chaves de categoria do backend (ordem de exibição) → rótulos, descrição e capa. */
const CATEGORY_META: Record<string, CategoryMeta> = {
  API_ANALISE_EXPLORATORIA: {
    pt: 'Análise Exploratória',
    en: 'Exploratory Analysis',
    descPt: 'Diagnósticos rápidos sobre indicadores e bases de dados.',
    descEn: 'Quick diagnostics on indicators and datasets.',
    cover: 'coral',
  },
  API_AVALIACAO_SIMULACAO: {
    pt: 'Avaliação e Simulação',
    en: 'Evaluation and Simulation',
    descPt: 'Estimativas de impacto e cenários contrafactuais.',
    descEn: 'Impact estimates and counterfactual scenarios.',
    cover: 'teal',
  },
  API_BENCHMARKING: {
    pt: 'Benchmarking',
    en: 'Benchmarking',
    descPt: 'Comparações nacionais e internacionais de políticas.',
    descEn: 'National and international policy comparisons.',
    cover: 'emerald',
  },
  API_POLICY_BRIEF: {
    pt: 'Policy Brief',
    en: 'Policy Brief',
    descPt: 'Resumos executivos com recomendações acionáveis.',
    descEn: 'Executive summaries with actionable recommendations.',
    cover: 'ocean',
  },
  API_SINTESE_MAPA_EVIDENCIAS: {
    pt: 'Síntese e Mapa de Evidências',
    en: 'Evidence Synthesis and Maps',
    descPt: 'Revisões sistemáticas e mapas de evidência.',
    descEn: 'Systematic reviews and evidence maps.',
    cover: 'ink',
  },
};

export const CATEGORY_ORDER = Object.keys(CATEGORY_META);

/** Chaves de tema do backend → rótulos. */
const TEMA_LABELS: Record<string, { pt: string; en: string }> = {
  API_ADM_PUBLICA: { pt: 'Administração Pública', en: 'Public Administration' },
  API_CIENCIA_TEC_INO_COMUNICAO: {
    pt: 'Ciência, Tecnologia e Inovação',
    en: 'Science, Technology and Innovation',
  },
  API_CULTURA: { pt: 'Cultura', en: 'Culture' },
  API_INC_PROT_DIREITOS_HUMANOS: { pt: 'Direitos Humanos', en: 'Human Rights' },
  API_PARAM_FILTRO_EDUCACAO: { pt: 'Educação', en: 'Education' },
  API_PARAM_FILTRO_SAUDE: { pt: 'Saúde', en: 'Health' },
  API_PERGUNTA_GRAFICO_OUTROS: { pt: 'Outros', en: 'Other' },
  API_TRABALHO: { pt: 'Trabalho', en: 'Labor' },
};

const isPt = (locale: Locale) => locale === 'pt-BR';

const MONTHS_PT = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
const MONTHS_EN = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

/** "JUN / 2025" a partir de `data_publicacao` (ISO) ou do ano. */
export function formatMonthYear(
  dataPublicacao: string | undefined,
  anoFallback: string | undefined,
  locale: Locale,
): string {
  if (dataPublicacao && dataPublicacao.length >= 7) {
    const year = dataPublicacao.slice(0, 4);
    const month = parseInt(dataPublicacao.slice(5, 7), 10);
    if (year && month >= 1 && month <= 12) {
      const months = isPt(locale) ? MONTHS_PT : MONTHS_EN;
      return `${months[month - 1]} / ${year}`;
    }
  }
  return anoFallback ?? '';
}

/** Prettifica uma chave desconhecida (fallback): API_FOO_BAR → "Foo Bar". */
function prettify(key: string): string {
  return key
    .replace(/^API_/, '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function categoryLabel(key: string, locale: Locale): string {
  const meta = CATEGORY_META[key];
  return meta ? (isPt(locale) ? meta.pt : meta.en) : prettify(key);
}

export function categoryDescription(key: string, locale: Locale): string {
  const meta = CATEGORY_META[key];
  return meta ? (isPt(locale) ? meta.descPt : meta.descEn) : '';
}

export function categoryCover(key: string): string {
  const cover = CATEGORY_META[key]?.cover ?? 'ink';
  return COVER_STYLES[cover];
}

export function temaLabel(key: string, locale: Locale): string {
  const meta = TEMA_LABELS[key];
  return meta ? (isPt(locale) ? meta.pt : meta.en) : prettify(key);
}

/** Temas presentes na lista (chaves distintas), ordenados pelo rótulo. */
export function distinctTemas(
  relatorios: RelatorioResponse[],
  locale: Locale,
): string[] {
  const keys = new Set<string>();
  for (const r of relatorios) {
    if (r.tema?.nome) keys.add(r.tema.nome);
  }
  return [...keys].sort((a, b) =>
    temaLabel(a, locale).localeCompare(temaLabel(b, locale), 'pt-BR'),
  );
}
